import { Component, DestroyRef, inject, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { Salesperson } from '@galaxy/types';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PackageService } from '@vendasta/marketplace-packages';
import {
  CreateOpportunityRequestCreateOpportunityPackageInterface,
  DurationInterface,
  Opportunity,
  SalesOpportunitiesSdk,
  SalesOpportunitiesService,
} from '@vendasta/sales-opportunities';
import { LineItem } from '@vendasta/sales-orders';
import { addMonths, endOfDay, startOfDay } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { SalesOrderConfirmationComponent } from '../../../sales-orders/sales-order-confirmation/sales-order-confirmation.component';
import { OpportunityValues } from '../../interface';
import {
  MARKET_ID_OVERRIDE_TOKEN,
  SALES_UI_CREATE_OPPORTUNITY_CONFIG_TOKEN,
  SalesUICreateOpportunityConfig,
} from '../../tokens';
import { getDefaultOpportunityType } from '../../type-selector/type-selector.component';
import { OpportunityBusinessService } from '../business-service/opportunity-business.service';
import { OpportunitySidePanelEventsService } from '../opportunity-side-panel-events.service';
import { CreateOpportunityService } from './create-opportunity.service';
import {
  CONTRACT_DURATION_FORM_NAME,
  CONTRACT_DURATION_FORM_PERIOD,
  CONTRACT_DURATION_FORM_VALUE,
  CreateOpportunityPageData,
  PartnerMarketConfig,
  TagOption,
} from './interface';
import { OrderToOpportunityLineItems } from '../../../sales-orders/conversion-utils';

@Component({
  selector: 'sales-ui-create-opportunity',
  templateUrl: './create-opportunity.component.html',
  styleUrls: ['./create-opportunity.component.scss'],
})
export class CreateOpportunityComponent implements OnInit {
  @ViewChild('stepper') private readonly stepper: MatStepper;
  protected partnerMarketConfig$: Observable<PartnerMarketConfig>;
  private readonly destroyRef = inject(DestroyRef);
  private bypassQuantityRestrictions$: Observable<boolean>;
  private defaultCurrency$: Observable<string>;
  private salesPersonId$: Observable<string>;
  private salesPeople$: Observable<Salesperson[]>;
  private data$: Observable<CreateOpportunityPageData>;

  // first step fields
  protected accountForm: FormGroup;
  protected accountCtrl: FormControl;
  protected readonly skipAccountSelection$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected readonly skipAccountSelection$: Observable<boolean> = this.skipAccountSelection$$.asObservable();

  // second step fields
  @ViewChild('salesOrderConfirmation') salesOrderConfirmation: SalesOrderConfirmationComponent;

  // third step fields
  additionalInfoForm: FormGroup;
  today: Date = new Date();
  tags$: Observable<TagOption[]>;
  sortedSalespeople$: Observable<Salesperson[]>;
  subscribers$: Observable<TagOption[]>;
  protected readonly defaultOpportunityType = getDefaultOpportunityType();

  //submit
  private saving$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly saving$: Observable<boolean> = this.saving$$.asObservable();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sidePanelEvents: OpportunitySidePanelEventsService,
    private readonly businessService: OpportunityBusinessService,
    private readonly snackbarService: SnackbarService,
    @Inject(SALES_UI_CREATE_OPPORTUNITY_CONFIG_TOKEN) private readonly config: SalesUICreateOpportunityConfig,
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
    private readonly packageService: PackageService,
    private readonly appPartnerService: AppPartnerService,
    private readonly createService: CreateOpportunityService,
    @Optional() @Inject(MARKET_ID_OVERRIDE_TOKEN) private readonly marketOverride: string,
  ) {
    const partnerId$ = this.config.partnerId$;
    const marketId$ = this.marketOverride ? of(this.marketOverride) : this.config.marketId$;
    this.bypassQuantityRestrictions$ = this.config.bypassQuantityRestrictions$;
    this.defaultCurrency$ = this.config.defaultCurrency$;
    this.salesPersonId$ = this.config.salespersonId$;
    this.salesPeople$ = this.config.salespeople$;
    this.data$ = this.createService.createPageDetails$;

    this.partnerMarketConfig$ = combineLatest([
      partnerId$,
      marketId$,
      this.bypassQuantityRestrictions$,
      this.defaultCurrency$,
    ]).pipe(
      map(
        ([partnerId, marketId, bypassQuantityLimit, currency]) =>
          ({ partnerId, marketId, bypassQuantityLimit, currency }) as PartnerMarketConfig,
      ),
    );
  }

  ngOnInit(): void {
    this.initFirstStep();
    this.initFinalStep();
    combineLatest([this.businessService.currentBusiness$, this.data$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([business, pageData]) => {
        if (business && pageData?.sourceLocation === 'company-profile') {
          this.skipAccountSelection$$.next(true);
          this.accountForm.controls.firstStepCtrl.setValue({
            companyName: business.name,
            accountGroupId: business.businessId,
          });
          this.accountForm.controls.firstStepCtrl.disable();
        }
      });
  }

  private initFirstStep(): void {
    this.accountCtrl = new FormControl('', Validators.required);
    this.accountForm = this.formBuilder.group({
      firstStepCtrl: this.accountCtrl,
    });
  }

  secondStepNext(): void {
    if (this.salesOrderConfirmation.isFormValid()) {
      // emits error snack if false
      this.stepper.next();
    }
  }

  private initFinalStep(): void {
    const monthFromToday = startOfDay(addMonths(this.today, 1));
    this.additionalInfoForm = this.formBuilder.group({
      pipelineAndStageCtrl: [undefined, Validators.required],
      expectedClosedDate: new FormControl(monthFromToday, Validators.required),
      opportunityName: new FormControl('', []),
      selectedSalesperson: new FormControl('', []),
      overviewNotes: new FormControl('', []),
      tags: new FormControl([], []),
      selectedSubscribers: new FormControl([], []),
      opportunityType: new FormControl(this.defaultOpportunityType.id),
    });

    // setup tags field
    this.tags$ = this.partnerMarketConfig$.pipe(
      switchMap((pm) => {
        return this.opportunityApi.getTags(pm.partnerId, pm.marketId);
      }),
      map((tags) => {
        return tags.map((t) => <TagOption>{ name: t, value: t });
      }),
    );

    // setup salesperson field
    this.sortedSalespeople$ = combineLatest([this.salesPeople$, this.salesPersonId$]).pipe(
      tap(([people, me]) => {
        const curSalesperson: Salesperson = people.find((sp) => sp.id === me);
        this.additionalInfoForm.controls.selectedSalesperson.setValue(curSalesperson);
      }),
      map(([people]) => {
        return people.sort((sp1, sp2) => sp1.fullName.localeCompare(sp2.fullName));
      }),
    );

    this.subscribers$ = this.sortedSalespeople$.pipe(
      map((people) => people.map((sp: Salesperson) => <TagOption>{ name: sp.fullName, value: sp.id })),
    );
  }

  setPipelineValues(values: OpportunityValues): void {
    this.additionalInfoForm.controls.pipelineAndStageCtrl.setValue(values);
  }

  compareSalespeople(sp1: Salesperson, sp2: Salesperson): boolean {
    return sp1?.id === sp2?.id;
  }

  handleTypeChanged(newType: string): void {
    this.additionalInfoForm.controls.opportunityType.setValue(newType);
  }

  private areFormsInvalid(): boolean {
    return this.accountForm.invalid || !this.salesOrderConfirmation.isFormValid() || this.additionalInfoForm.invalid;
  }

  private autoGenerateOpportunityNameFromLineItem$(li: LineItem, pm: PartnerMarketConfig): Observable<string> {
    return li.packageId
      ? this.getPackageName(li.packageId)
      : this.getAppName(li.appKey?.appId, li.appKey?.editionId, pm);
  }

  private getPackageName(packageId: string): Observable<string> {
    return this.packageService.getMulti([packageId]).pipe(map((packages) => packages[0]?.name));
  }

  private getAppName(appId: string, editionId: string, pm: PartnerMarketConfig): Observable<string> {
    return this.appPartnerService
      .getMulti(
        [
          new AppKey({
            appId: appId,
            editionId: editionId,
          }),
        ],
        pm.partnerId,
        pm.marketId,
        null,
      )
      .pipe(map((apps) => apps[0]?.sharedMarketingInformation?.name));
  }

  private get packages(): CreateOpportunityRequestCreateOpportunityPackageInterface[] {
    return this.salesOrderConfirmation
      .getCurrentLineItems()
      .filter((item) => !!item.packageId)
      .map(
        (p) =>
          <CreateOpportunityRequestCreateOpportunityPackageInterface>{
            packageId: p.packageId,
            quantity: p.quantity,
          },
      );
  }

  private get contractDuration(): DurationInterface {
    const form = this.additionalInfoForm.controls[CONTRACT_DURATION_FORM_NAME];
    const value = form?.value[CONTRACT_DURATION_FORM_VALUE] || 0;
    const duration = form?.value[CONTRACT_DURATION_FORM_PERIOD] || 0;
    if (value == 0 && duration == 0) return null;
    return { value, duration };
  }

  private isContractDurationInvalid(): boolean {
    const form = this.additionalInfoForm.controls[CONTRACT_DURATION_FORM_NAME];
    const value = form?.value[CONTRACT_DURATION_FORM_VALUE] || 0;
    const duration = form?.value[CONTRACT_DURATION_FORM_PERIOD] || 0;

    return (value === 0 && duration !== 0) || (value !== 0 && duration === 0);
  }

  saveHandler(partnerMarket: PartnerMarketConfig): void {
    const lineItems = this.salesOrderConfirmation.getCurrentLineItems();
    this.saving$$.next(true);
    if (lineItems?.length === 0) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.OPPORTUNITY_ITEMS_REQUIRED');
      this.saving$$.next(false);
      return;
    }

    if (this.areFormsInvalid()) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.FORMS.INVALID_FORM');
      this.saving$$.next(false);
      return;
    }

    if (this.isContractDurationInvalid()) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.FORMS.INVALID_CONTACT_DURATION');
      this.saving$$.next(false);
      return;
    }

    const subscriberUserIds = this.additionalInfoForm.controls.selectedSubscribers?.value;
    const assigneeId = this.additionalInfoForm.controls.selectedSalesperson?.value?.id;

    if (subscriberUserIds?.length > 5) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.ERRORS.EXCEED_SUBSCRIBERS_LIMIT');
      this.saving$$.next(false);
      return;
    }

    if (subscriberUserIds?.includes(assigneeId)) {
      this.snackbarService.openErrorSnack(
        'FRONTEND.SALES_UI.SALES_OPPORTUNITIES.ERRORS.INVALID_ASSIGNEE_AND_SUBSCRIBERS',
      );
      this.saving$$.next(false);
      return;
    }
    this.autoGenerateOpportunityNameFromLineItem$(lineItems[0], partnerMarket)
      .pipe(
        take(1),
        switchMap((autoGenOppNameFromLineItem) => {
          return this.createOpportunity(lineItems, partnerMarket, autoGenOppNameFromLineItem);
        }),
      )
      .subscribe(
        (o: Opportunity) => this.onSubmitSuccess(o),
        () => {
          this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.ERRORS.GENERIC_ERROR');
        },
      )
      .add(() => {
        this.saving$$.next(false);
      });
  }

  private createOpportunity(items: LineItem[], pm: PartnerMarketConfig, nameFallback: string): Observable<Opportunity> {
    const rawExpectedCloseDate = this.additionalInfoForm.controls.expectedClosedDate.value;
    const expectCloseDate = zonedTimeToUtc(endOfDay(rawExpectedCloseDate), 'UTC');
    return this.opportunityApi.create(
      this.accountForm.controls.firstStepCtrl.value.accountGroupId,
      this.additionalInfoForm.controls.selectedSalesperson.value.id,
      pm.marketId,
      pm.partnerId,
      expectCloseDate,
      this.additionalInfoForm.controls.pipelineAndStageCtrl.value.probability,
      this.additionalInfoForm.controls.pipelineAndStageCtrl.value.pipelineId,
      this.additionalInfoForm.controls.opportunityName.value || nameFallback,
      this.packages,
      this.additionalInfoForm.controls.overviewNotes.value,
      this.additionalInfoForm.controls.tags.value,
      this.contractDuration,
      OrderToOpportunityLineItems(items),
      this.additionalInfoForm.controls.selectedSubscribers.value,
      this.additionalInfoForm.controls.opportunityType.value,
    );
  }

  private onSubmitSuccess(o: Opportunity): void {
    this.snackbarService.openSuccessSnack('FRONTEND.SALES_UI.SALES_OPPORTUNITIES.SUCCESSFUL_CREATION');
    this.sidePanelEvents.opportunityCreated(o);
  }

  closeSideNav(): void {
    this.sidePanelEvents.sidePanelCloseRequested();
  }
}
